'use client';

import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { signOut } from '@/lib/actions/auth';
import { Popover, MenuItem, MenuSeparator } from '@/components/ui/popover';
import styles from './profile-menu.module.css';

interface ProfileMenuProps {
  /** Two-letter account initials shown in the avatar (spec `.cp-appbar__avatar`). */
  initials: string;
  /** Display name shown in the menu's identity header, when known. */
  name?: string | null;
  /** Account email shown under the name in the identity header. */
  email?: string | null;
}

/**
 * Header profile menu — an identity header (avatar · name · email) over the
 * account actions, with Sign out set apart below a divider and danger-tinted.
 * Dismiss + keyboard behaviour come from the shared Popover primitive.
 */
export function ProfileMenu({ initials, name, email }: ProfileMenuProps) {
  const t = useTranslations('nav');
  const hasIdentity = Boolean(name || email);

  return (
    <Popover
      align="end"
      role="menu"
      label={t('profileMenuAria')}
      trigger={
        <button type="button" className={styles.trigger} aria-label={t('openProfileMenu')}>
          {initials}
        </button>
      }
    >
      {(close) => (
        <>
          {hasIdentity && (
            <>
              <div className={styles.identity}>
                <span className={styles.identityAvatar} aria-hidden="true">
                  {initials}
                </span>
                <span className={styles.identityText}>
                  {name && <span className={styles.identityName}>{name}</span>}
                  {email && <span className={styles.identityEmail}>{email}</span>}
                </span>
              </div>
              <MenuSeparator />
            </>
          )}
          <MenuItem as={Link} href="/profile" leadingIcon="user" onClick={close}>
            {t('profile')}
          </MenuItem>
          <MenuItem as={Link} href="/settings" leadingIcon="settings" onClick={close}>
            {t('settings')}
          </MenuItem>
          <MenuItem as={Link} href="/cookbook/jobs" leadingIcon="list" onClick={close}>
            {t('generationHistory')}
          </MenuItem>
          <MenuSeparator />
          <form action={signOut} className={styles.form}>
            <MenuItem type="submit" leadingIcon="logout" danger>
              {t('signOut')}
            </MenuItem>
          </form>
        </>
      )}
    </Popover>
  );
}
